import { Box, Button, Flex, Grid, GridItem, IconButton, Input, Text } from "@chakra-ui/react";
import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import { TExistingLineQuantity } from "../../contexts/types";
import { EXISTING_LINE_QUANTITY_MAX_ROWS } from "../../hooks/useExistingLineQuantitySpreadSheet";
import { CustomNumberInput } from "./CustomNumberInput";

type Props = {
  /** 年月ラベル（"YYYY/MM"、量産開始〜EOL） */
  labels: string[];
  /** 既存ライン生産数量 */
  data: TExistingLineQuantity;
  /** 変更時のコールバック */
  onChange: (data: TExistingLineQuantity) => void;
  /** 入力不可（GoogleSpread入力中・権限なし） */
  isDisabled?: boolean;
};

/**
 * 生産キャパグラフ：既存ラインPartNoの生産予定数量の画面入力欄
 * 行 = PartNo（追加・削除可、最大 EXISTING_LINE_QUANTITY_MAX_ROWS 行）、列 = 年月。空欄は未入力として扱う
 * 入力中の値はこのコンポーネント内で保持し、フォーカスアウト時のみ親へ反映する
 * （1文字ごとに親画面の再描画・グラフ再計算が走るのを防ぐため）
 */
export const ExistingLineQuantityInput = memo(function ExistingLineQuantityInput({ labels, data, onChange, isDisabled }: Props) {
  const { t } = useTranslation();

  // 入力中の値（親のデータが変わったら同期する）
  const [draft, setDraft] = useState<TExistingLineQuantity>(data);
  useEffect(() => {
    setDraft(data);
  }, [data]);

  /** 入力中の値を親へ反映する */
  const commit = (next: TExistingLineQuantity) => {
    setDraft(next);
    // 変更がなければ親へ通知しない（Tab移動だけで再計算が走らないように）
    if (JSON.stringify(next) !== JSON.stringify(data)) {
      onChange(next);
    }
  };

  /** PartNoを変更する（入力中の値のみ更新） */
  const updatePartNo = (rowIndex: number, partNo: string) => {
    setDraft(prev => prev.map((row, i) => (i === rowIndex ? { ...row, partNo } : row)));
  };

  /** 数量を変更した結果を返す（空欄はキーを削除。表示範囲外のキーは保持） */
  const applyQuantity = (source: TExistingLineQuantity, rowIndex: number, label: string, value: number | null) => {
    return source.map((row, i) => {
      if (i !== rowIndex) return row;
      const quantities = { ...row.quantities };
      if (value === null) {
        delete quantities[label];
      } else {
        quantities[label] = value;
      }
      return { ...row, quantities };
    });
  };

  /** 行を追加する（上限行数まで） */
  const addRow = () => {
    if (draft.length >= EXISTING_LINE_QUANTITY_MAX_ROWS) return;
    commit([...draft, { partNo: "", quantities: {} }]);
  };

  /** 行を削除する */
  const removeRow = (rowIndex: number) => {
    commit(draft.filter((_, i) => i !== rowIndex));
  };

  // 左固定列（−ボタン＋PartNo）。横スクロールしても常に表示し、下を流れる数量欄が透けないよう背景を塗る
  // 右側の列間（gap 16px）も影で塗りつぶし、行間は gap ではなく上下の余白で取る（行間から透けるのを防ぐ）
  const stickyCellProps = {
    position: "sticky" as const,
    left: 0,
    zIndex: 1,
    bg: "white",
    boxShadow: "16px 0 0 white",
    py: 2,
  };

  return (
    <Box mt={4}>
      <Text fontWeight="bold" mb={2}>
        {t('features.InvestmentScreening.SimulationNewProduct.label.existingLineQuantity')}
      </Text>
      {draft.length > 0 && (
        <Box overflowX="auto" height="fit-content">
          <Grid templateColumns={`206px repeat(${labels.length}, 150px)`} columnGap={4} rowGap={0} alignItems="center" width="fit-content">
            {/* ヘッダー */}
            <GridItem {...stickyCellProps}>
              <Flex gap={4} align="center">
                <Box width="40px" flexShrink={0} />
                <Text fontWeight="bold">PartNo</Text>
              </Flex>
            </GridItem>
            {labels.map(label => {
              const [year, month] = label.split("/").map(Number);
              return (
                <Text textAlign="center" fontWeight="bold" key={`header-${label}`}>
                  {t('common.dateFormat.yearMonth', { year, month })}
                </Text>
              );
            })}

            {/* 数量行（PartNo別） */}
            {draft.map((row, rowIndex) => (
              <React.Fragment key={`row-${rowIndex}`}>
                <GridItem {...stickyCellProps}>
                  <Flex gap={4} align="center">
                    <Box width="40px" flexShrink={0}>
                      <IconButton
                        aria-label="remove"
                        icon={<FaCircleMinus />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        isDisabled={isDisabled}
                        onClick={() => removeRow(rowIndex)}
                      />
                    </Box>
                    <Input
                      width="150px"
                      value={row.partNo}
                      isDisabled={isDisabled}
                      onChange={(e) => updatePartNo(rowIndex, e.target.value)}
                      onBlur={() => commit(draft)}
                    />
                  </Flex>
                </GridItem>
                {labels.map(label => (
                  <GridItem key={`quantity-${rowIndex}-${label}`}>
                    <CustomNumberInput
                      value={row.quantities[label] ?? null}
                      isDisabled={isDisabled}
                      onChange={(value) => setDraft(prev => applyQuantity(prev, rowIndex, label, value))}
                      onBlur={(value) => commit(applyQuantity(draft, rowIndex, label, value))}
                      max={9999999999}
                      min={0}
                      decimalPlaces={0}
                    />
                  </GridItem>
                ))}
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      )}
      <Box mt={2} display="flex" alignItems="center" gap={3}>
        <Button
          size="sm"
          leftIcon={<FaPlusCircle />}
          isDisabled={isDisabled || draft.length >= EXISTING_LINE_QUANTITY_MAX_ROWS}
          onClick={addRow}
        >
          {t('features.InvestmentScreening.SimulationNewProduct.button.addExistingLineRow')}
        </Button>
        <Text fontSize="sm" color="gray.600">
          {t('features.InvestmentScreening.SimulationNewProduct.label.existingLineMaxRows', { max: EXISTING_LINE_QUANTITY_MAX_ROWS })}
        </Text>
      </Box>
    </Box>
  );
});
